import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listarSaldos } from '../../api/cadastros-do-erp.js';
import type { FiltroDeSaldos, SaldoDoErp } from '../../api/cadastros-do-erp.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { filialDoFiltro, rotuloDoLocal } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../../componentes/Filtros.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';
import { IconeArmazem } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { formatarQuantidade } from '../movimentacoes/quantidade.js';
import { useValorAdiado } from '../movimentacoes/useValorAdiado.js';
import { CodigoDoProduto, DatasDaSincronizacao, SituacaoNoErp } from './partes.js';
import {
  OPCOES_DE_SITUACAO_DO_CADASTRO,
  SITUACAO_PADRAO_DO_CADASTRO,
  SITUACOES_DO_CADASTRO,
  TODOS,
  valorDoSeletor,
} from './situacao.js';

const OPCOES_DE_DIVERGENCIA: readonly OpcaoFiltro[] = [
  { valor: TODOS, rotulo: 'Divergência · todos' },
  { valor: 'true', rotulo: 'Com divergência pendente' },
  { valor: 'false', rotulo: 'Sem divergência' },
];

/** O link leva o recorte junto: a tela das divergências abre já neste saldo. */
function linkDaDivergencia(saldo: SaldoDoErp): string {
  const parametros = new URLSearchParams({
    produto: saldo.codPro,
    codFil: String(saldo.codFil),
    deposito: saldo.codDep,
  });
  return `/saldos/divergencias?${parametros.toString()}`;
}

/** Quantidade em texto, sem arredondar; `null` é "o ERP não informa". */
function QuantidadeDoSaldo({
  rotulo,
  valor,
  destaque = false,
}: {
  readonly rotulo: string;
  readonly valor: string | null;
  readonly destaque?: boolean;
}) {
  return (
    <span
      className={destaque ? 'quantidade quantidade--destaque' : 'quantidade'}
      aria-label={rotulo}
    >
      {valor === null ? '—' : formatarQuantidade(valor)}
    </span>
  );
}

function LocalDoSaldo({ saldo }: { readonly saldo: SaldoDoErp }) {
  return (
    <span className="sincronizacao__pilha">
      <span className="mono">{rotuloDoLocal(saldo.codFil, saldo.codDep)}</span>
      {saldo.codLot !== '' && <span className="sincronizacao__discreto">Lote {saldo.codLot}</span>}
      {saldo.numSep !== '' && <span className="sincronizacao__discreto">Série {saldo.numSep}</span>}
    </span>
  );
}

const COLUNAS: readonly Coluna<SaldoDoErp>[] = [
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '20%',
    celula: (saldo) => (
      <CodigoDoProduto codPro={saldo.codPro} codDer={saldo.codDer} nome={saldo.produtoNome} />
    ),
  },
  {
    chave: 'local',
    rotulo: 'Local',
    largura: '14%',
    celula: (saldo) => <LocalDoSaldo saldo={saldo} />,
  },
  {
    chave: 'disponivel',
    rotulo: 'Disponível',
    largura: '86px',
    alinhamento: 'direita',
    celula: (saldo) => (
      <QuantidadeDoSaldo rotulo="Disponível" valor={saldo.quantidadeDisponivel} destaque />
    ),
  },
  {
    chave: 'reservada',
    rotulo: 'Reservada',
    largura: '80px',
    alinhamento: 'direita',
    celula: (saldo) => <QuantidadeDoSaldo rotulo="Reservada" valor={saldo.quantidadeReservada} />,
  },
  {
    chave: 'bloqueada',
    rotulo: 'Bloqueada',
    largura: '80px',
    alinhamento: 'direita',
    celula: (saldo) => <QuantidadeDoSaldo rotulo="Bloqueada" valor={saldo.quantidadeBloqueada} />,
  },
  {
    chave: 'transito',
    rotulo: 'Em trânsito',
    largura: '80px',
    alinhamento: 'direita',
    celula: (saldo) => (
      <QuantidadeDoSaldo rotulo="Em trânsito" valor={saldo.quantidadeEmTransito} />
    ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    largura: '170px',
    celula: (saldo) => (
      <SituacaoNoErp ativo={saldo.ativo}>
        {saldo.divergenciaPendenteId !== null && (
          <>
            <ChipStatus tom="divergencia" rotulo="Divergência pendente" tamanho="tabela" />
            <Link className="sincronizacao__link" to={linkDaDivergencia(saldo)}>
              Decidir a divergência
            </Link>
          </>
        )}
      </SituacaoNoErp>
    ),
  },
  {
    chave: 'datas',
    rotulo: 'Datas',
    celula: (saldo) => (
      <DatasDaSincronizacao
        atualizadoEm={saldo.atualizadoEm}
        sincronizadoEm={saldo.sincronizadoEm}
      />
    ),
  },
];

/** A divergência pendente espera gente — a linha não pode passar despercebida. */
function classeDaLinha(saldo: SaldoDoErp): string | undefined {
  return saldo.divergenciaPendenteId === null ? undefined : 'sincronizacao__linha--atencao';
}

/**
 * Os saldos por filial + depósito + produto como a sincronização os gravou do
 * ERP. Saldo que veio diferente do daqui não muda sozinho: fica com a
 * divergência pendente, e o link leva à tela onde alguém decide.
 */
export function AbaSaldos() {
  const [produto, definirProduto] = useState('');
  const [filial, definirFilial] = useState('');
  const [deposito, definirDeposito] = useState('');
  const [situacao, definirSituacao] = useState<string>(SITUACAO_PADRAO_DO_CADASTRO);
  const [divergencia, definirDivergencia] = useState(TODOS);
  const [pagina, definirPagina] = useState(1);
  const produtoAdiado = useValorAdiado(produto);
  const depositoAdiado = useValorAdiado(deposito);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeSaldos>(
    () => ({
      pagina,
      ...seInformado('produto', produtoAdiado),
      ...seInformado('codFil', codFil),
      ...seInformado('deposito', depositoAdiado),
      ...seInformado('situacao', valorDoSeletor(situacao, SITUACOES_DO_CADASTRO)),
      ...seInformado('comDivergencia', divergencia === TODOS ? null : divergencia === 'true'),
    }),
    [pagina, produtoAdiado, codFil, depositoAdiado, situacao, divergencia],
  );

  const buscar = useCallback(() => listarSaldos(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros dos saldos">
        <CampoBusca
          rotulo="Buscar por produto"
          valor={produto}
          largura={200}
          aoMudar={(valor) => {
            recortar(() => {
              definirProduto(valor);
            });
          }}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        <CampoBusca
          rotulo="Buscar por depósito"
          valor={deposito}
          largura={180}
          aoMudar={(valor) => {
            recortar(() => {
              definirDeposito(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO_DO_CADASTRO}
          aoMudar={(valor) => {
            recortar(() => {
              definirSituacao(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Divergência"
          valor={divergencia}
          opcoes={OPCOES_DE_DIVERGENCIA}
          aoMudar={(valor) => {
            recortar(() => {
              definirDivergencia(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'saldo', 'saldos')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando os saldos…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeArmazem tamanho={22} />}
            titulo="Nenhum saldo neste recorte."
            detalhe="Os saldos chegam do ERP por filial e depósito, pela carga inicial e pela sincronização periódica."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(saldo) => saldo.id}
            descricao="Saldos do ERP"
            classeDaLinha={classeDaLinha}
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'saldo', 'saldos')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
